/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-19 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { FullscreenBar, Page, Tabs, Heading, Button, TextStyle } from '@shopify/polaris';

import StoreV1Tab from './store-v1';
import StoreV2Tab from './store-v2';
import StoreV1SSRTab from './store-v1-ssr';
import StoreV2SSRTab from './store-v2-ssr';

import {
  exitSABFullscreen,
  enterSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { URL } from 'src/constants/links';
import { getFullAppUrlThemeEditor } from 'src/helpers';
import {
  navigateToAppBlockSSR,
  navigateToThemeCodeEditor
} from 'src/helpers/shopify-integration-helpers';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { handleOpenFullscreen } from 'actions/app-integration-actions';

import history from 'src/routes/history';
import { routes } from 'src/routes';

import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

import noteIcon from 'src/assets/images/dashboard-v3/get-support/note-icon.png';
import questionIcon from 'src/assets/images/dashboard-v3/get-support/question-icon.png';
import watchIcon from 'src/assets/images/dashboard-v3/get-support/watch-icon.png';

const StepGuideInstallTheme = props => {
  const {
    i18n,
    themes,
    getAllThemes,
    handleOpenFullscreen,
    checkEnableBoostCore,
    enabledBoostCore,
    isEnableShopifyIntegration,
    templateId,
    checkThemeIntegrationStatus
  } = props;

  const [selected, setSelected] = useState(0);

  const mainTheme = themes.find(theme => theme.role === 'main') || themes[0] || {};
  const themeId = _.get(mainTheme, 'id', 0);

  // Use effect - Before render page
  useEffect(() => {
    if (themes.length <= 0) {
      getAllThemes();
    }
    const unsubscribeExit = enterSABFullscreen(handleExit);
    handleOpenFullscreen(true);

    // Check enable boost core on live theme
    checkEnableBoostCore({});
    return () => {
      unsubscribeExit();
    };
  }, []);

  // Check main tab
  useEffect(() => {
    const isOS20 = _.get(mainTheme, 'isOS20', true);
    setSelected(isOS20 ? 0 : 1);
    // Get SSR status
    if (themeId && isEnableShopifyIntegration) {
      checkThemeIntegrationStatus(themeId);
    }
  }, [themes]);

  // handle exit page
  const handleExit = () => {
    history.push(routes.recommendationWidgets.list());
    exitSABFullscreen();
    handleOpenFullscreen(false);
  };

  // handle enable boost core
  const handleOpenBoostCore = () => {
    const url = getFullAppUrlThemeEditor(themeId, '?context=apps');
    window.open(url, '_blank');
  };

  // handle open theme editor
  const handleOpenThemeEditor = () => {
    const url = getFullAppUrlThemeEditor(themeId);
    window.open(url, '_blank');
  };

  // handle enable boost core ssr
  const handleOpenBoostCoreSSR = () => {
    navigateToAppBlockSSR(themeId, '', templateId, '', true);
  };

  // handle open theme edit code page
  const handleOpenEditCode = () => {
    navigateToThemeCodeEditor(themeId);
  };

  // Open new page with link
  const openLink = (id: string) => {
    const url = URL[id];
    window.open(url, '_blank');
  };
  // Init value tabs
  const tabs = [
    {
      id: 'online-store-2.0',
      content: i18n.translate('AdminHTML.Recommendation.WidgetStepsGuideInstall.tab02.title'),
      panelID: 'online-store-2.0-tab'
    },
    {
      id: 'online-store-1.0',
      content: i18n.translate('AdminHTML.Recommendation.WidgetStepsGuideInstall.tab01.title'),
      panelID: 'online-store-1.0-tab'
    }
  ];
  const tabComponents = isEnableShopifyIntegration
    ? [
        <StoreV2SSRTab
          handleOpenBoostCoreSSR={handleOpenBoostCoreSSR}
          handleOpenBoostCore={handleOpenBoostCore}
          handleOpenThemeEditor={handleOpenThemeEditor}
          enabledBoostCore={enabledBoostCore}
          templateId={templateId}
        />,
        <StoreV1SSRTab
          handleOpenBoostCoreSSR={handleOpenBoostCoreSSR}
          handleOpenBoostCore={handleOpenBoostCore}
          handleOpenEditCode={handleOpenEditCode}
          enabledBoostCore={enabledBoostCore}
          templateId={templateId}
        />
      ]
    : [
        <StoreV2Tab
          handleOpenBoostCore={handleOpenBoostCore}
          handleOpenThemeEditor={handleOpenThemeEditor}
          enabledBoostCore={enabledBoostCore}
        />,
        <StoreV1Tab handleOpenBoostCore={handleOpenBoostCore} enabledBoostCore={enabledBoostCore} />
      ];

  const usefulGuides = [
    [
      {
        label: 'AdminHTML.DashboardV3.GetSupport.section02_Card01',
        image: questionIcon,
        action: () => openLink('HelpCenterOS2')
      },
      {
        label: 'AdminHTML.DashboardV3.GetSupport.section02_Card02',
        image: watchIcon,
        action: () => openLink('RecommendationTutorialVideo')
      },
      {
        label: 'AdminHTML.DashboardV3.GetSupport.section02_Card03',
        image: noteIcon,
        action: () => openLink('Link_V2_Recommendation_ManageWidget_BestPractice')
      }
    ],
    [
      {
        label: 'AdminHTML.DashboardV3.GetSupport.section02_Card01',
        image: questionIcon,
        action: () => openLink('HelpCenterOS1')
      },
      {
        label: 'AdminHTML.DashboardV3.GetSupport.section02_Card03',
        image: noteIcon,
        action: () => openLink('Link_V2_Recommendation_ManageWidget_BestPractice')
      }
    ]
  ];
  return (
    <div className="BC-Page__RW-StepGuide">
      <FullscreenBar onAction={handleExit}>
        <div className="fullscreen-bar">
          <Button primary onClick={handleExit}>
            {i18n.translate('AdminHTML.Recommendation.WidgetStepsGuideInstall.action02')}
          </Button>
        </div>
      </FullscreenBar>
      <Page>
        <div className="Polaris-Card section01">
          <Heading>
            {i18n.translate('AdminHTML.Recommendation.WidgetStepsGuideInstall.title')}
          </Heading>
          <Tabs tabs={tabs} selected={selected} onSelect={value => setSelected(value)}>
            <div style={{ marginTop: '1.5rem' }}>{tabComponents[selected]}</div>
          </Tabs>
        </div>
        <div className="Polaris-Card section02">
          <Heading>
            {i18n.translate('AdminHTML.Recommendation.WidgetStepsGuideInstall.usefulGuide_Title')}
          </Heading>
          <p>
            {i18n.translate(
              'AdminHTML.Recommendation.WidgetStepsGuideInstall.usefulGuide_SubTitle'
            )}
          </p>
          <div className="useful">
            {usefulGuides[selected].map((card, cardIndex) => {
              return (
                <div onClick={card.action} className="card-action" key={`card-${cardIndex}`}>
                  <img width={20} height={20} src={card.image} alt={card.label} />
                  <TextStyle variation="strong">{i18n.translate(card.label)}</TextStyle>
                </div>
              );
            })}
          </div>
        </div>
      </Page>
    </div>
  );
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n)),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  getAllThemes: () => dispatch(themeSetupV3Action.getAllThemes(ownProps.i18n)),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  return {
    enabledBoostCore: _.get(state.recommendation, 'isEnableBoostCore', false),
    themes: _.get(state.themeSetupV3, 'allThemes', []),
    isEnableShopifyIntegration,
    templateId: _.get(state.themeIntegration, 'templateInstance.templateId', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(StepGuideInstallTheme));
